import { DeviceType } from 'src/types/DeviceType';
import { DeviceCatalogIndex, DeviceCatalogResponse, DeviceTypeCapability } from 'src/types/DeviceTypeCapability';

/**
 * Module-level snapshot of the latest device-type catalog.
 *
 * `useDeviceCatalog` keeps this in sync with the fetched catalog so the synchronous helpers
 * (`getDeviceIcon`, `formatDeviceName`) can read catalog capabilities without being React hooks. It's
 * empty until the catalog first loads, so callers degrade gracefully (icons fall back to unknown, names
 * fall back to the static label).
 */

export const indexByType = (catalog: DeviceCatalogResponse): DeviceCatalogIndex =>
  catalog.deviceTypes.reduce<DeviceCatalogIndex>((index, capability) => {
    index[capability.type] = capability;
    return index;
  }, {});

let cache: DeviceCatalogIndex = {};

export const setDeviceCatalogCache = (index: DeviceCatalogIndex): void => {
  cache = index;
};

export const getDeviceCatalogCache = (): DeviceCatalogIndex => cache;

/**
 * Capability lookup against a SUPPLIED index — identical normalisation to `getDeviceCapability`,
 * but pure.
 *
 * Prefer this inside a render, passing the index from `useDeviceCatalog()`: the result is then
 * reactive and the dependency on the catalog is visible to the compiler, instead of resting on
 * this module's cache having been populated earlier in the same render by a hook call above.
 */
export const findCapability = (index: DeviceCatalogIndex, deviceType?: string): DeviceTypeCapability | undefined =>
  deviceType ? index[deviceType.toUpperCase() as DeviceType] : undefined;

export const getDeviceCapability = (deviceType?: string): DeviceTypeCapability | undefined =>
  findCapability(cache, deviceType);
